import { z } from "zod";

/**
 * A push token is only half an address. The other half is which of Apple's two services
 * to send through, and that follows the BUILD, not the phone — a dev or staging build is
 * signed for the sandbox, a store build is not. Sending to the wrong one is accepted and
 * silently dropped, so the environment travels with the token and the sender reads it back.
 */
export const registerDeviceSchema = z.object({
  token: z.string().min(16),
  platform: z.enum(["ios", "android"]),
  appEnv: z.enum(["dev", "staging", "production"]),
  /** So a stale build's tokens can be found later without guessing. */
  appVersion: z.string(),
  locale: z.string().min(2).max(10).optional(),
});

export type RegisterDevicePayload = z.infer<typeof registerDeviceSchema>;

export const deviceSchema = z.object({
  id: z.string(),
  platform: z.enum(["ios", "android"]),
  appEnv: z.enum(["dev", "staging", "production"]),
  appVersion: z.string(),
  lastSeenAt: z.iso.datetime(),
});

export type DeviceDTO = z.infer<typeof deviceSchema>;

/**
 * What a test push reports back, per handset. `token` is deliberately absent: it is a push
 * CREDENTIAL, and anyone holding it can notify that handset for as long as it stays valid.
 */
export const pushResultSchema = z.object({
  appEnv: z.enum(["dev", "staging", "production"]),
  ok: z.boolean(),
  reason: z.string().nullable(),
});

export const testPushSchema = z.object({ sent: z.array(pushResultSchema) });

export type PushResult = z.infer<typeof pushResultSchema>;
export type TestPushDTO = z.infer<typeof testPushSchema>;
